import React from 'react';
import { Button } from '@planne-software/uni/Button';

import { useTranslate } from '@src/core/hooks';
import type { TariffUnitGroup } from '@src/domains/orders';
import type { DefaultMessagesKeys } from '@src/i18n/useTranslate';

import { TariffRowLabels, TariffRowName, TariffRowRoot, TariffRowStatus } from '../styles';

type TariffRowProps = {
  tariff: TariffUnitGroup;
  filledUnits: number;
  hasError: boolean;
  /** Which unit the wizard lands on is decided by the accordion, which holds the values. */
  onFill: () => void;
  /** Editing opens the units of this row at once, instead of stepping through them. */
  onEdit: () => void;
};

const resolveStatusI18nKey = (complete: boolean, hasError: boolean, filledUnits: number): DefaultMessagesKeys => {
  if (hasError) {
    return 'checkout_additional_info_needs_review';
  }

  if (complete) {
    return 'checkout_additional_info_filled';
  }

  return filledUnits > 0 ? 'checkout_additional_info_partially_filled' : 'checkout_additional_info_not_filled';
};

const resolveActionI18nKey = (complete: boolean, hasError: boolean): DefaultMessagesKeys => {
  if (hasError) {
    return 'checkout_additional_info_review';
  }

  return complete ? 'checkout_additional_info_edit' : 'checkout_additional_info_fill';
};

export const TariffRow = (props: TariffRowProps) => {
  const { tariff, filledUnits, hasError, onFill, onEdit } = props;
  const { i18n } = useTranslate();

  const total = tariff.units.length;
  const complete = total > 0 && filledUnits === total;

  return (
    <TariffRowRoot data-testid={`tariff-row-${tariff.tariffId}`}>
      <TariffRowLabels>
        <TariffRowName>{`${total}x ${tariff.name}`}</TariffRowName>
        <TariffRowStatus data-testid='tariff-row-status' data-tone={hasError ? 'error' : 'default'}>
          {i18n(resolveStatusI18nKey(complete, hasError, filledUnits), {
            filled: String(filledUnits),
            total: String(total),
          })}
        </TariffRowStatus>
      </TariffRowLabels>

      <Button
        variant={complete && !hasError ? 'text' : 'outlined'}
        // Complete row: edit opens every unit at once. Incomplete or rejected: the wizard
        // takes over, starting at the first unit still missing.
        onClick={complete && !hasError ? onEdit : onFill}
      >
        {i18n(resolveActionI18nKey(complete, hasError))}
      </Button>
    </TariffRowRoot>
  );
};

export default TariffRow;
